import { useQuery } from "@tanstack/react-query";
import { CACHE_TIMES } from "@/lib/queryClient";
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

interface StyleVariables {
  uoCream: string;
  uoGold: string;
  uoSilver: string;
  uoCharcoal: string;
  uoMidnight: string;
  uoOlive: string;
  uoBrown: string;
  uoDark: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  cardBorder: string;
  popover: string;
  popoverForeground: string;
  popoverBorder: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarRing: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  headerBg: string;
  headerFg: string;
  footerBg: string;
  footerFg: string;
  legalBg: string;
  legalFg: string;
  quotationBarBg: string;
  quotationBarFg: string;
  heroText: string;
  heroSubtext: string;
  inputBg: string;
  inputFg: string;
  inputBorder: string;
}

interface HeadingStyle {
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  letterSpacing?: string;
}

interface Typography {
  fontSans: string;
  fontBrand: string;
  fontMono: string;
  fontSerif?: string;
  h1?: HeadingStyle;
  h2?: HeadingStyle;
  h3?: HeadingStyle;
  h4?: HeadingStyle;
  h5?: HeadingStyle;
  body?: { fontSize: string; fontWeight: string; lineHeight: string };
}

interface ButtonStyle {
  bg: string;
  fg: string;
  hoverBg: string;
  borderRadius: string;
}

interface Buttons {
  ctaDark: ButtonStyle;
  ctaLight: ButtonStyle;
  outline: { borderColor: string; fg: string; hoverBg: string; borderRadius: string };
  ghost: { fg: string; hoverBg: string; borderRadius: string };
}

interface StyleProfile {
  id: string;
  name: string;
  isActive: boolean;
  variables: StyleVariables;
  typography: Typography;
  buttons?: Buttons;
}

function isValidHex(hex: string): boolean {
  if (!hex || typeof hex !== 'string') return false;
  const hexPattern = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
  return hexPattern.test(hex);
}

function expandShortHex(hex: string): string {
  if (hex.length === 4) {
    return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }
  return hex;
}

function hexToHsl(value: string): string {
  if (!value || typeof value !== 'string') return value;
  
  if (!value.startsWith('#')) {
    return value;
  }
  
  if (!isValidHex(value)) {
    return value;
  }
  
  const hex = expandShortHex(value);
  
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return value;
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const cssVariableMap: Record<keyof StyleVariables, string> = {
  uoCream: '--uo-cream',
  uoGold: '--uo-gold',
  uoSilver: '--uo-silver',
  uoCharcoal: '--uo-charcoal',
  uoMidnight: '--uo-midnight',
  uoOlive: '--uo-olive',
  uoBrown: '--uo-brown',
  uoDark: '--uo-dark',
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  cardBorder: '--card-border',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  popoverBorder: '--popover-border',
  sidebar: '--sidebar',
  sidebarForeground: '--sidebar-foreground',
  sidebarBorder: '--sidebar-border',
  sidebarPrimary: '--sidebar-primary',
  sidebarPrimaryForeground: '--sidebar-primary-foreground',
  sidebarAccent: '--sidebar-accent',
  sidebarAccentForeground: '--sidebar-accent-foreground',
  sidebarRing: '--sidebar-ring',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  destructive: '--destructive',
  destructiveForeground: '--destructive-foreground',
  border: '--border',
  input: '--input',
  ring: '--ring',
  headerBg: '--header-bg',
  headerFg: '--header-fg',
  footerBg: '--footer-bg',
  footerFg: '--footer-fg',
  legalBg: '--legal-bg',
  legalFg: '--legal-fg',
  quotationBarBg: '--quotation-bar-bg',
  quotationBarFg: '--quotation-bar-fg',
  heroText: '--uo-hero-text',
  heroSubtext: '--uo-hero-subtext',
  inputBg: '--uo-input-bg',
  inputFg: '--uo-input-fg',
  inputBorder: '--uo-input-border',
};

export function usePublicStyles() {
  const [location] = useLocation();
  const originalValuesRef = useRef<Record<string, string | null>>({});
  const storedVarsRef = useRef<Set<string>>(new Set());
  const hasAppliedStylesRef = useRef(false);
  
  const isCrmRoute = location.startsWith('/crm-secure');
  
  const { data: profile, isLoading, error } = useQuery<StyleProfile | null>({
    queryKey: ['/api/styles/active'],
    staleTime: CACHE_TIMES.catalog,
    refetchOnWindowFocus: false,
    enabled: !isCrmRoute,
  });

  useEffect(() => {
    if (isCrmRoute) {
      if (hasAppliedStylesRef.current) {
        const root = document.documentElement;
        storedVarsRef.current.forEach((cssVar) => {
          const originalValue = originalValuesRef.current[cssVar];
          if (originalValue !== null && originalValue !== undefined) {
            root.style.setProperty(cssVar, originalValue);
          } else {
            root.style.removeProperty(cssVar);
          }
        });
        hasAppliedStylesRef.current = false;
        originalValuesRef.current = {};
        storedVarsRef.current.clear();
      }
      return;
    }
    
    if (!profile) return;

    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    const applyVariable = (cssVar: string, value: string, transform?: (v: string) => string) => {
      if (!value) return;
      if (!storedVarsRef.current.has(cssVar)) {
        const computed = computedStyle.getPropertyValue(cssVar);
        originalValuesRef.current[cssVar] = computed || null;
        storedVarsRef.current.add(cssVar);
      }
      const finalValue = transform ? transform(value) : value;
      root.style.setProperty(cssVar, finalValue);
    };

    // Apply color variables
    if (profile.variables) {
      Object.entries(cssVariableMap).forEach(([key, cssVar]) => {
        const value = profile.variables[key as keyof StyleVariables];
        if (value) {
          applyVariable(cssVar, value, hexToHsl);
        }
      });
    }
    
    // Apply typography variables
    if (profile.typography) {
      const typo = profile.typography;
      
      // Font families
      if (typo.fontSans) applyVariable('--font-sans', typo.fontSans);
      if (typo.fontBrand) applyVariable('--font-brand', typo.fontBrand);
      if (typo.fontMono) applyVariable('--font-mono', typo.fontMono);
      if (typo.fontSerif) applyVariable('--font-serif', typo.fontSerif);
      
      // Heading styles (as CSS custom properties for use in components)
      const applyHeadingStyle = (prefix: string, style?: HeadingStyle) => {
        if (!style) return;
        if (style.fontSize) applyVariable(`--${prefix}-font-size`, style.fontSize);
        if (style.fontWeight) applyVariable(`--${prefix}-font-weight`, style.fontWeight);
        if (style.lineHeight) applyVariable(`--${prefix}-line-height`, style.lineHeight);
        if (style.letterSpacing) applyVariable(`--${prefix}-letter-spacing`, style.letterSpacing);
      };
      
      applyHeadingStyle('h1', typo.h1);
      applyHeadingStyle('h2', typo.h2);
      applyHeadingStyle('h3', typo.h3);
      applyHeadingStyle('h4', typo.h4);
      applyHeadingStyle('h5', typo.h5);
      
      // Body text
      if (typo.body) {
        if (typo.body.fontSize) applyVariable('--body-font-size', typo.body.fontSize);
        if (typo.body.fontWeight) applyVariable('--body-font-weight', typo.body.fontWeight);
        if (typo.body.lineHeight) applyVariable('--body-line-height', typo.body.lineHeight);
      }
    }
    
    // Apply button styles
    if (profile.buttons) {
      const btns = profile.buttons;
      
      // CTA Dark button
      if (btns.ctaDark) {
        if (btns.ctaDark.bg) applyVariable('--cta-dark-bg', btns.ctaDark.bg, hexToHsl);
        if (btns.ctaDark.fg) applyVariable('--cta-dark-fg', btns.ctaDark.fg, hexToHsl);
        if (btns.ctaDark.hoverBg) applyVariable('--cta-dark-hover-bg', btns.ctaDark.hoverBg, hexToHsl);
        if (btns.ctaDark.borderRadius) applyVariable('--cta-dark-radius', btns.ctaDark.borderRadius);
      }
      
      // CTA Light button
      if (btns.ctaLight) {
        if (btns.ctaLight.bg) applyVariable('--cta-light-bg', btns.ctaLight.bg, hexToHsl);
        if (btns.ctaLight.fg) applyVariable('--cta-light-fg', btns.ctaLight.fg, hexToHsl);
        if (btns.ctaLight.hoverBg) applyVariable('--cta-light-hover-bg', btns.ctaLight.hoverBg, hexToHsl);
        if (btns.ctaLight.borderRadius) applyVariable('--cta-light-radius', btns.ctaLight.borderRadius);
      }
      
      // Outline button
      if (btns.outline) {
        if (btns.outline.borderColor) applyVariable('--btn-outline-border', btns.outline.borderColor, hexToHsl);
        if (btns.outline.fg) applyVariable('--btn-outline-fg', btns.outline.fg, hexToHsl);
        if (btns.outline.hoverBg) applyVariable('--btn-outline-hover-bg', btns.outline.hoverBg, hexToHsl);
        if (btns.outline.borderRadius) applyVariable('--btn-outline-radius', btns.outline.borderRadius);
      }
      
      // Ghost button
      if (btns.ghost) {
        if (btns.ghost.fg) applyVariable('--btn-ghost-fg', btns.ghost.fg, hexToHsl);
        if (btns.ghost.hoverBg) applyVariable('--btn-ghost-hover-bg', btns.ghost.hoverBg, hexToHsl);
        if (btns.ghost.borderRadius) applyVariable('--btn-ghost-radius', btns.ghost.borderRadius);
      }
    }
    
    hasAppliedStylesRef.current = true;

    return () => {
      storedVarsRef.current.forEach((cssVar) => {
        const originalValue = originalValuesRef.current[cssVar];
        if (originalValue !== null && originalValue !== undefined) {
          root.style.setProperty(cssVar, originalValue);
        } else {
          root.style.removeProperty(cssVar);
        }
      });
      hasAppliedStylesRef.current = false;
    };
  }, [profile, isCrmRoute]);

  return { profile, isLoading, error };
}
